import React from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import ConfirmationModal, { ModalBase } from 'src/components/ConfirmationModal';
import { useLcaOrganization } from 'src/common/hooks';
import {
  useLcaOrganizationSettings,
  useUpdateLcaOrganizationSettings,
} from '../hooks';

interface LcaSettingsDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function LcaSettingsDialog({
  open,
  onClose,
}: LcaSettingsDialogProps) {
  const { t } = useTranslation('lca', { keyPrefix: 'lcaSettings' });
  const organization = useLcaOrganization();
  const { settings } = useLcaOrganizationSettings(
    open ? organization.id : undefined
  );
  const updateSettings = useUpdateLcaOrganizationSettings(organization.id);
  const [version, setVersion] = React.useState('');
  const [isConfirming, setIsConfirming] = React.useState(false);

  const currentVersion = settings?.default_ecoinvent_version ?? '';
  const canEdit = !!settings?.can_edit;
  const isChanged = !!version && version !== currentVersion;

  React.useEffect(() => {
    if (open) setVersion(currentVersion);
  }, [open, currentVersion]);

  const save = () => {
    updateSettings
      .mutateAsync(version)
      .then(() => {
        toast.success(t('saved') as string);
        setIsConfirming(false);
        onClose();
      })
      .catch(() => toast.error(t('saveError') as string));
  };

  return (
    <>
      <ModalBase
        open={open && !isConfirming}
        onClose={onClose}
        titleKey="lca:lcaSettings.title"
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            minWidth: '420px',
            maxWidth: '560px',
          }}
        >
          <Typography variant="h4">{t('ecoinventVersion.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('ecoinventVersion.description')}
          </Typography>
          <FormControl fullWidth>
            <InputLabel id="lca-settings-ecoinvent-version">
              {t('ecoinventVersion.label')}
            </InputLabel>
            <Select
              labelId="lca-settings-ecoinvent-version"
              label={t('ecoinventVersion.label')}
              value={version}
              disabled={!canEdit}
              onChange={(e) => setVersion(e.target.value)}
            >
              {(settings?.available_ecoinvent_versions ?? []).map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {settings && !canEdit && (
            <Alert severity="info">{t('ecoinventVersion.adminOnly')}</Alert>
          )}
        </Box>
        <DialogActions
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            paddingTop: '30px',
            paddingBottom: 0,
          }}
        >
          <Button
            onClick={onClose}
            variant="secondary"
            sx={{ minWidth: '200px' }}
          >
            <Trans i18nKey="common.cancel" />
          </Button>
          {canEdit && (
            <Button
              onClick={() => setIsConfirming(true)}
              disabled={!isChanged}
              sx={{ minWidth: '200px' }}
            >
              {t('save')}
            </Button>
          )}
        </DialogActions>
      </ModalBase>
      <ConfirmationModal
        open={open && isConfirming}
        titleKey="lca:lcaSettings.confirm.title"
        trueButtonTextKey="lca:lcaSettings.confirm.submit"
        falseButtonTextKey="common.cancel"
        onTrue={save}
        onFalse={() => setIsConfirming(false)}
        onClose={() => setIsConfirming(false)}
      >
        <Alert severity="warning" sx={{ maxWidth: '560px' }}>
          <Typography>
            {t('confirm.resultsWarning', {
              from: currentVersion,
              to: version,
            })}
          </Typography>
          <Typography sx={{ mt: '8px' }}>
            {t('confirm.noRecalculation')}
          </Typography>
        </Alert>
      </ConfirmationModal>
    </>
  );
}
